import { useState, useCallback, useRef } from 'react';

/**
 * Hook for geocoding addresses to GPS coordinates using OpenStreetMap Nominatim API.
 * Free to use, no API key required.
 * Rate limit: 1 request per second (handled with debouncing)
 * 
 * IMPORTANT: Includes country code bias to improve accuracy for local addresses.
 * Default is Greece (gr) - change COUNTRY_CODE for other regions.
 */

// Default country code for geocoding bias - Greece
// This helps ensure addresses like "Airport" return Greek airports, not random ones worldwide
const COUNTRY_CODES = 'gr';  // Greece - change to your country code (e.g., 'it' for Italy, 'es' for Spain)
const VIEWBOX = '19.0,34.5,30.0,42.0';  // Bounding box for Greece (minLon,minLat,maxLon,maxLat)

export function useGeocoding() {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(null);
  const lastRequestTime = useRef(0);
  const pendingRequest = useRef(null);

  /**
   * Geocode an address to coordinates
   * @param {string} address - The address to geocode
   * @param {boolean} useCountryBias - Whether to bias results to the configured country (default: true)
   * @returns {Promise<{lat: number, lng: number, displayName: string} | null>}
   */
  const geocodeAddress = useCallback(async (address, useCountryBias = true) => {
    if (!address || address.trim().length < 3) {
      return null;
    }

    // Rate limiting: ensure at least 1 second between requests
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime.current;
    if (timeSinceLastRequest < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastRequest));
    }

    setIsGeocoding(true);
    setGeocodeError(null);
    lastRequestTime.current = Date.now();

    try {
      // Use OpenStreetMap Nominatim API (free, no API key required)
      const encodedAddress = encodeURIComponent(address);
      
      // Build URL with country bias for more accurate local results
      let url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=5`;
      
      if (useCountryBias) {
        // Add country code and viewbox to bias results to the configured region
        url += `&countrycodes=${COUNTRY_CODES}`;
        url += `&viewbox=${VIEWBOX}&bounded=0`;  // bounded=0 allows results outside viewbox but prefers inside
      }
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'OutsidersTravel/1.0' // Required by Nominatim TOS
        }
      });

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        // Return the first (best) result
        const result = data[0];
        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          displayName: result.display_name,
          // Include all results for potential selection
          allResults: data.map(r => ({
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
            displayName: r.display_name,
            type: r.type,
            importance: r.importance
          }))
        };
      }
      
      // If no results with country bias, try without (fallback for international addresses)
      if (useCountryBias) {
        console.log('No results with country bias, trying global search...');
        return geocodeAddress(address, false);
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      setGeocodeError(error.message);
      return null;
    } finally {
      setIsGeocoding(false);
    }
  }, []);

  /**
   * Debounced geocoding - waits for user to stop typing
   * @param {string} address - The address to geocode
   * @param {number} delay - Delay in ms (default 800ms)
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  const geocodeAddressDebounced = useCallback((address, delay = 800) => {
    return new Promise((resolve) => {
      // Cancel any pending request
      if (pendingRequest.current) {
        clearTimeout(pendingRequest.current);
      }

      pendingRequest.current = setTimeout(async () => {
        const result = await geocodeAddress(address);
        resolve(result);
      }, delay);
    });
  }, [geocodeAddress]);

  /**
   * Geocode multiple addresses in sequence (respecting rate limits)
   * @param {string[]} addresses - Array of addresses to geocode
   * @returns {Promise<Array<{lat: number, lng: number} | null>>}
   */
  const geocodeMultiple = useCallback(async (addresses) => {
    const results = [];
    for (const address of addresses) {
      const result = await geocodeAddress(address);
      results.push(result);
    }
    return results;
  }, [geocodeAddress]);

  return {
    geocodeAddress,
    geocodeAddressDebounced,
    geocodeMultiple,
    isGeocoding,
    geocodeError
  };
}

export default useGeocoding;
